import React from "react";
import { RefreshCw, Home } from "lucide-react";

/**
 * App-wide error boundary. A component crash used to unmount the whole tree
 * (blank white page). Now it renders a branded recovery screen, reports the
 * error (Sentry when configured, console otherwise) and offers reload/home.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    try {
      if (window.Sentry?.captureException) window.Sentry.captureException(error, { extra: info });
      else console.error("[ErrorBoundary]", error, info?.componentStack);
    } catch (_) {}
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] text-stone-900 p-6">
        <div className="max-w-md w-full rounded-3xl border border-stone-200 bg-white p-10 text-center" data-testid="error-boundary">
          <p className="text-xs font-mono uppercase tracking-widest text-[#C2410C]">Something went wrong</p>
          <h1 className="font-display text-3xl mt-3">This page hit a snag.</h1>
          <p className="text-sm text-stone-600 mt-3 leading-relaxed">
            Nothing you did is lost — the error has been logged. Reload the page, or head back to your dashboard.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-900 text-white text-sm font-medium hover:bg-[#C2410C]">
              <RefreshCw size={14} /> Reload
            </button>
            <a href="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-stone-300 text-sm font-medium text-stone-700 hover:border-stone-900">
              <Home size={14} /> Home
            </a>
          </div>
        </div>
      </div>
    );
  }
}
